// PlatformKeyInfoCard — Dashboard explainer.
//
// Feb 27, 2026 — rewritten with the new 3-provider BYOK story.
//
// What we want creators to walk away understanding:
// 1. Their subscription buys ALL the platform tools + creation freedom
// 2. LLM calls are a separate (tiny) per-query cost — typically 1–2¢
// 3. We've tuned routing + caching so digital-twin queries stay cheap
// 4. Heavy power tools (Workbench / Refinery / Boost) tap enterprise-tier
//    models that cost more per call — but most creators rarely need them
// 5. BYOK (Gemini + Anthropic + OpenAI) drops those costs up to 80% by
//    going direct, no middleman. Same call, smaller bill.

import { useNavigate } from "react-router-dom";
import { Cpu, ArrowRight, Lightning, Sparkle, CurrencyDollar } from "@phosphor-icons/react";
import { usePublicPricing, fmt$ } from "@/lib/usePublicPricing";

export default function PlatformKeyInfoCard() {
  const nav = useNavigate();
  const pricing = usePublicPricing();

  const goToFaq = () => {
    try { sessionStorage.setItem("dashboard_tab", "faq"); } catch { /* noop */ }
    if (window.location.pathname === "/dashboard") {
      window.dispatchEvent(new CustomEvent("dashboard-scroll-to", { detail: { hash: "#faq-earn-money", tab: "faq" } }));
    } else {
      nav("/dashboard#faq-earn-money");
    }
  };

  const scrollToBYOK = () => {
    const el = document.getElementById("creator-byok-providers-card");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-2", "ring-[#7A5200]", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#7A5200]", "ring-offset-2"), 1800);
    }
  };

  return (
    <div id="platform-info" className="bg-white border hairline rounded-xl p-6 scroll-mt-28" data-testid="platform-info-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <Cpu size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-[#1C1B1A]">Your subscription vs LLM usage — the real breakdown</h3>
          <p className="text-xs text-[#6B6A68] mt-1.5 leading-relaxed">
            Your <strong>{fmt$(pricing.platform_fee_cents)}/mo subscription</strong> covers every tool, feature, and creation freedom on My Silo — building your digital twin, hosting it, sharing it, all of it. The one thing it doesn't pay for: the LLM calls your twin makes when fans ask it questions. That's a real metered cost from Google / Anthropic / OpenAI, billed per query.
          </p>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-[#FDFCF8] border hairline rounded-md p-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#2F6B3A] mb-1">
                <Sparkle size={10} weight="duotone" /> Everyday twin queries
              </div>
              <div className="text-xs text-[#1C1B1A] leading-relaxed">
                Heavily tuned routing + caching keep typical fan-facing questions at <strong>~1–2¢ each</strong>. Most silos burn just a few dollars a month here.
              </div>
            </div>
            <div className="bg-[#FDFCF8] border hairline rounded-md p-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#7A5200] mb-1">
                <Lightning size={10} weight="duotone" /> Power tools (when you need them)
              </div>
              <div className="text-xs text-[#1C1B1A] leading-relaxed">
                Workbench + Refinery + <strong>Boost</strong> tap enterprise-grade models normally reserved for big companies. Heavier calls, heavier cost — but the option's there when you want to push a project hard.
              </div>
            </div>
            <div className="bg-[#FDFCF8] border hairline rounded-md p-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#4A5D4E] mb-1">
                <CurrencyDollar size={10} weight="duotone" /> BYOK = save up to 80%
              </div>
              <div className="text-xs text-[#1C1B1A] leading-relaxed">
                Connect your own Gemini / Claude / GPT keys below and your calls bill <strong>direct merchant rate — no middleman</strong>. Same calls, ~3× cheaper.
              </div>
            </div>
          </div>

          <p className="text-[11px] text-[#6B6A68] mt-4 leading-relaxed max-w-3xl">
            <strong>The honest pitch:</strong> most creators rarely touch the power tools — daily twin Q&A on the platform key is plenty. But when you do want to dial things up with Boost, that's when bringing your own keys pays for itself fast.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={scrollToBYOK}
              data-testid="platform-info-byok-cta"
              className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] inline-flex items-center gap-1.5 font-medium">
              Connect your keys <ArrowRight size={12} weight="bold" />
            </button>
            <button
              onClick={goToFaq}
              data-testid="platform-info-faq-link"
              className="text-xs text-[#4A5D4E] hover:text-[#1C1B1A] inline-flex items-center gap-1 font-medium">
              How creators earn money on My Silo <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
